import {
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationCounts,
  type NotificationsListResponse,
} from '@nomiss/services';
import { notificationQueryKeys } from './use-notifications';

type NotificationsInfiniteData = InfiniteData<
  NotificationsListResponse,
  string | undefined
>;

function patchItemRead(
  data: NotificationsInfiniteData | undefined,
  id: string,
): NotificationsInfiniteData | undefined {
  if (!data) {
    return data;
  }

  return {
    ...data,
    pages: data.pages.map(page => ({
      ...page,
      items: page.items.map(item =>
        item.id === id
          ? {
              ...item,
              isRead: true,
              readAt: item.readAt ?? new Date().toISOString(),
            }
          : item,
      ),
    })),
  };
}

function patchAllItemsRead(
  data: NotificationsInfiniteData | undefined,
): NotificationsInfiniteData | undefined {
  if (!data) {
    return data;
  }

  const now = new Date().toISOString();

  return {
    ...data,
    pages: data.pages.map(page => ({
      ...page,
      items: page.items.map(item => ({
        ...item,
        isRead: true,
        readAt: item.readAt ?? now,
      })),
    })),
  };
}

function wasUnreadInCache(
  lists: Array<[unknown, NotificationsInfiniteData | undefined]>,
  id: string,
): boolean {
  for (const [, data] of lists) {
    const match = data?.pages
      .flatMap(page => page.items)
      .find(item => item.id === id);
    if (match) {
      return !match.isRead;
    }
  }
  return false;
}

export function useMarkNotificationRead(accessToken?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id, accessToken),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({
        queryKey: notificationQueryKeys.allLists,
      });

      const previousLists = queryClient.getQueriesData<NotificationsInfiniteData>(
        { queryKey: notificationQueryKeys.allLists },
      );
      const previousCounts = queryClient.getQueriesData<NotificationCounts>({
        queryKey: notificationQueryKeys.counts,
      });
      const shouldDecrementUnread = wasUnreadInCache(previousLists, id);

      queryClient.setQueriesData<NotificationsInfiniteData>(
        { queryKey: notificationQueryKeys.allLists },
        data => patchItemRead(data, id),
      );

      if (shouldDecrementUnread) {
        queryClient.setQueriesData<NotificationCounts>(
          { queryKey: notificationQueryKeys.counts },
          counts =>
            counts
              ? {
                  ...counts,
                  unread: Math.max(0, counts.unread - 1),
                }
              : counts,
        );
      }

      return { previousLists, previousCounts };
    },
    onError: (_error, _id, context) => {
      if (!context) {
        return;
      }

      for (const [key, data] of context.previousLists) {
        queryClient.setQueryData(key, data);
      }

      for (const [key, data] of context.previousCounts) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.allLists,
      });
      void queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.counts,
      });
    },
  });
}

export function useMarkAllNotificationsRead(accessToken?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => markAllNotificationsRead(accessToken),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: notificationQueryKeys.allLists,
      });
      await queryClient.cancelQueries({
        queryKey: notificationQueryKeys.counts,
      });

      const previousLists = queryClient.getQueriesData<NotificationsInfiniteData>(
        { queryKey: notificationQueryKeys.allLists },
      );
      const previousCounts = queryClient.getQueriesData<NotificationCounts>({
        queryKey: notificationQueryKeys.counts,
      });

      queryClient.setQueriesData<NotificationsInfiniteData>(
        { queryKey: notificationQueryKeys.allLists },
        data => patchAllItemsRead(data),
      );

      queryClient.setQueriesData<NotificationCounts>(
        { queryKey: notificationQueryKeys.counts },
        counts =>
          counts
            ? {
                ...counts,
                unread: 0,
              }
            : counts,
      );

      return { previousLists, previousCounts };
    },
    onError: (_error, _vars, context) => {
      if (!context) {
        return;
      }

      for (const [key, data] of context.previousLists) {
        queryClient.setQueryData(key, data);
      }

      for (const [key, data] of context.previousCounts) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.allLists,
      });
      void queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.counts,
      });
    },
  });
}
